/**
 * Service for tracking student behavior analytics during test taking
 */

export interface QuestionTimingData {
  questionId: string;
  subject: string;
  topic?: string;
  difficultyLevel?: string;
  timeSpentMs: number;
  visitCount: number;
  firstVisitTime: string; // ISO string
  lastVisitTime: string; // ISO string
  wasAnswered: boolean;
  wasMarkedForReview: boolean;
}

export interface NavigationEvent {
  timestamp: string; // ISO string
  fromQuestionId?: string;
  toQuestionId: string;
  fromQuestionIndex?: number;
  toQuestionIndex: number;
  navigationType: 'NEXT' | 'PREVIOUS' | 'JUMP' | 'SUBJECT_SWITCH' | 'PALETTE_CLICK';
  timeOnPreviousQuestionMs?: number;
}

export interface SubjectSwitchEvent {
  timestamp: string; // ISO string
  fromSubject?: string;
  toSubject: string;
  switchNumber: number;
  timeSinceLastSwitchMs?: number;
}

export interface AnswerChangeEvent {
  timestamp: string; // ISO string
  questionId: string;
  previousAnswer?: string;
  newAnswer?: string;
  changeNumber: number;
  timeSinceQuestionFirstVisitMs: number;
}

export interface ReviewEvent {
  timestamp: string; // ISO string
  questionId: string;
  marked: boolean;
  hadAnswer: boolean;
}

export interface FocusEvent {
  timestamp: string; // ISO string
  eventType: 'FOCUS_LOST' | 'FOCUS_GAINED' | 'TAB_SWITCH' | 'WINDOW_BLUR';
  durationMs?: number;
  currentQuestionId?: string;
}

export interface BehaviorAnalyticsData {
  sessionStartTime: string; // ISO string
  sessionEndTime?: string; // ISO string
  questionTimings: QuestionTimingData[];
  navigationEvents: NavigationEvent[];
  subjectSwitches: SubjectSwitchEvent[];
  answerChanges: AnswerChangeEvent[];
  reviewEvents: ReviewEvent[];
  focusEvents: FocusEvent[];
}

class BehaviorAnalyticsService {
  private sessionStartTime: Date | null = null;
  private questionTimings: Map<string, QuestionTimingData> = new Map();
  private navigationEvents: NavigationEvent[] = [];
  private subjectSwitches: SubjectSwitchEvent[] = [];
  private answerChanges: AnswerChangeEvent[] = [];
  private reviewEvents: ReviewEvent[] = [];
  private focusEvents: FocusEvent[] = [];
  
  private currentQuestionId: string | null = null;
  private currentQuestionStartTime: Date | null = null;
  private currentSubject: string | null = null;
  private lastSubjectSwitchTime: Date | null = null;
  private subjectSwitchCount: number = 0;
  
  private answerChangeCountPerQuestion: Map<string, number> = new Map();
  private questionFirstVisitTime: Map<string, Date> = new Map();
  
  private focusLostTime: Date | null = null;

  /**
   * Initialize analytics tracking for a new test session
   */
  startSession(): void {
    this.sessionStartTime = new Date();
    this.questionTimings.clear();
    this.navigationEvents = [];
    this.subjectSwitches = [];
    this.answerChanges = [];
    this.reviewEvents = [];
    this.focusEvents = [];
    this.currentQuestionId = null;
    this.currentQuestionStartTime = null;
    this.currentSubject = null;
    this.lastSubjectSwitchTime = null;
    this.subjectSwitchCount = 0;
    this.answerChangeCountPerQuestion.clear();
    this.questionFirstVisitTime.clear();
    this.focusLostTime = null;
  }

  /**
   * Track question visit
   */
  trackQuestionVisit(
    questionId: string,
    questionIndex: number,
    subject: string,
    topic?: string,
    difficultyLevel?: string,
    fromQuestionId?: string,
    fromQuestionIndex?: number,
    navigationType: NavigationEvent['navigationType'] = 'NEXT'
  ): void {
    const now = new Date();
    
    // Save time spent on previous question
    let timeOnPreviousMs: number | undefined;
    if (this.currentQuestionId && this.currentQuestionStartTime) {
      timeOnPreviousMs = now.getTime() - this.currentQuestionStartTime.getTime();
      this.updateQuestionTiming(this.currentQuestionId, timeOnPreviousMs);
    }
    
    // Track navigation event
    this.navigationEvents.push({
      timestamp: now.toISOString(),
      fromQuestionId,
      toQuestionId: questionId,
      fromQuestionIndex,
      toQuestionIndex: questionIndex,
      navigationType,
      timeOnPreviousQuestionMs: timeOnPreviousMs
    });
    
    // Initialize or update question timing data
    if (!this.questionTimings.has(questionId)) {
      this.questionTimings.set(questionId, {
        questionId,
        subject,
        topic,
        difficultyLevel,
        timeSpentMs: 0,
        visitCount: 0,
        firstVisitTime: now.toISOString(),
        lastVisitTime: now.toISOString(),
        wasAnswered: false,
        wasMarkedForReview: false
      });
      this.questionFirstVisitTime.set(questionId, now);
    }
    
    const timing = this.questionTimings.get(questionId)!;
    timing.visitCount++;
    timing.lastVisitTime = now.toISOString();
    
    // Track subject switch
    if (this.currentSubject && this.currentSubject !== subject) {
      this.subjectSwitchCount++;
      const timeSinceLastSwitch = this.lastSubjectSwitchTime 
        ? now.getTime() - this.lastSubjectSwitchTime.getTime()
        : undefined;
      
      this.subjectSwitches.push({
        timestamp: now.toISOString(),
        fromSubject: this.currentSubject,
        toSubject: subject,
        switchNumber: this.subjectSwitchCount,
        timeSinceLastSwitchMs: timeSinceLastSwitch
      });
      
      this.lastSubjectSwitchTime = now;
    }
    
    this.currentQuestionId = questionId;
    this.currentQuestionStartTime = now;
    this.currentSubject = subject;
  }

  /**
   * Update question timing
   */
  private updateQuestionTiming(questionId: string, additionalTimeMs: number): void {
    const timing = this.questionTimings.get(questionId);
    if (timing) {
      timing.timeSpentMs += additionalTimeMs;
    }
  }

  /**
   * Track answer change
   */
  trackAnswerChange(questionId: string, previousAnswer: string | null, newAnswer: string | null): void {
    const now = new Date();
    const changeCount = (this.answerChangeCountPerQuestion.get(questionId) || 0) + 1;
    this.answerChangeCountPerQuestion.set(questionId, changeCount);
    
    const firstVisitTime = this.questionFirstVisitTime.get(questionId);
    const timeSinceFirstVisit = firstVisitTime 
      ? now.getTime() - firstVisitTime.getTime()
      : 0;
    
    this.answerChanges.push({
      timestamp: now.toISOString(),
      questionId,
      previousAnswer: previousAnswer || undefined,
      newAnswer: newAnswer || undefined,
      changeNumber: changeCount,
      timeSinceQuestionFirstVisitMs: timeSinceFirstVisit
    });
    
    // Update question timing data
    const timing = this.questionTimings.get(questionId);
    if (timing) {
      timing.wasAnswered = newAnswer !== null;
    }
  }

  /**
   * Track review marking
   */
  trackReviewMark(questionId: string, marked: boolean, hasAnswer: boolean): void {
    this.reviewEvents.push({
      timestamp: new Date().toISOString(),
      questionId,
      marked,
      hadAnswer: hasAnswer
    });
    
    // Update question timing data
    const timing = this.questionTimings.get(questionId);
    if (timing) {
      timing.wasMarkedForReview = marked;
    }
  }

  /**
   * Track focus loss
   */
  trackFocusLoss(eventType: FocusEvent['eventType']): void {
    this.focusLostTime = new Date();
    this.focusEvents.push({
      timestamp: this.focusLostTime.toISOString(),
      eventType,
      currentQuestionId: this.currentQuestionId || undefined
    });
  }

  /**
   * Track focus gain
   */
  trackFocusGain(): void {
    if (this.focusLostTime) {
      const now = new Date();
      const durationMs = now.getTime() - this.focusLostTime.getTime();
      
      this.focusEvents.push({
        timestamp: now.toISOString(),
        eventType: 'FOCUS_GAINED',
        durationMs,
        currentQuestionId: this.currentQuestionId || undefined
      });
      
      this.focusLostTime = null;
    }
  }

  /**
   * Get all analytics data for submission
   */
  getAnalyticsData(): BehaviorAnalyticsData {
    // Update timing for current question
    if (this.currentQuestionId && this.currentQuestionStartTime) {
      const timeSpent = new Date().getTime() - this.currentQuestionStartTime.getTime();
      this.updateQuestionTiming(this.currentQuestionId, timeSpent);
    }
    
    return {
      sessionStartTime: this.sessionStartTime?.toISOString() || new Date().toISOString(),
      sessionEndTime: new Date().toISOString(),
      questionTimings: Array.from(this.questionTimings.values()),
      navigationEvents: this.navigationEvents,
      subjectSwitches: this.subjectSwitches,
      answerChanges: this.answerChanges,
      reviewEvents: this.reviewEvents,
      focusEvents: this.focusEvents
    };
  }

  /**
   * Reset analytics data
   */
  reset(): void {
    this.sessionStartTime = null;
    this.questionTimings.clear();
    this.navigationEvents = [];
    this.subjectSwitches = [];
    this.answerChanges = [];
    this.reviewEvents = [];
    this.focusEvents = [];
    this.currentQuestionId = null;
    this.currentQuestionStartTime = null;
    this.currentSubject = null;
    this.lastSubjectSwitchTime = null;
    this.subjectSwitchCount = 0;
    this.answerChangeCountPerQuestion.clear();
    this.questionFirstVisitTime.clear();
    this.focusLostTime = null;
  }
}

// Export singleton instance
const behaviorAnalyticsService = new BehaviorAnalyticsService();
export default behaviorAnalyticsService;

